import React from 'react';
import { useTranslation } from 'react-i18next';
import { PATH } from '@/main/controller/MainController';
import logoSrc from '@/common/resource/logo/aace_512.png';
import Icon from '@/common/view/icons';
import MenuItem from './MenuItem';
import SubMenuItem from './SubMenuItem';
import ProfileItem from './ProfileItem';
import { Link } from 'react-router-dom';
import MainSetting from '../model/MainSetting';
import styled from 'styled-components';
import colors from '@/common/view/colors';
import { useSidebarStore } from '@/main/datacontroller/SidebarStore';

const SidebarContainer = styled.div<{ $collapsed: boolean }>`
  position: relative;
  display: inline-flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: start;
  flex: 0 0 ${({ $collapsed }) => ($collapsed ? '56px' : '210px')};
  width: ${({ $collapsed }) => ($collapsed ? '56px' : '210px')};
  border-right: 1px solid ${colors.gray30};
  background: ${colors.grayWhite};
  transition: flex-basis 250ms ease-in-out, width 250ms ease-in-out;
  overflow: hidden;
`;

const LogoWrapper = styled.div<{ $collapsed: boolean }>`
  width: 100%;
  height: 60px;
  padding: ${({ $collapsed }) => ($collapsed ? '17px 0 17px' : '16px 12px 12px 24px')};
  display: flex;
  justify-content: ${({ $collapsed }) => ($collapsed ? 'center' : 'space-between')};
  align-items: center;

  img {
    height: 32px;
    overflow: hidden;
  }
`;

const ToggleIcon = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  color: ${colors.gray50};
  padding: 4px;
  border-radius: 4px;

  &:hover {
    color: ${colors.blue50};
    background: ${colors.blue10};
  }
`;

const MainMenuView: React.FC = () => {
  const { t } = useTranslation();
  const { collapsed, toggleCollapsed } = useSidebarStore();

  return (
    <SidebarContainer $collapsed={collapsed}>
      <div className="flex flex-col w-full">
        <LogoWrapper $collapsed={collapsed}>
          {collapsed ? (
            <ToggleIcon onClick={toggleCollapsed} aria-label="사이드바 펼치기">
              <Icon.PanelLeftOpen width={18} height={18} />
            </ToggleIcon>
          ) : (
            <>
              <Link to={PATH.MAIN}>
                <img src={logoSrc} alt="AACE Logo" />
              </Link>
              <ToggleIcon onClick={toggleCollapsed} aria-label="사이드바 접기">
                <Icon.PanelLeftClose width={18} height={18} />
              </ToggleIcon>
            </>
          )}
        </LogoWrapper>
        <ul className="list-none p-0">
          <MenuItem title={t('gnb.menu.캠페인')} href={PATH.CAMPAIGN_INDEX} Icon={Icon.Home} collapsed={collapsed} />
          <MenuItem
            title={t('gnb.menu.오디언스')}
            href={PATH.TARGET_AUDIENCE_INDEX}
            Icon={Icon.Audience}
            collapsed={collapsed}
          />
          <MenuItem
            title={t('gnb.menu.콘텐츠')}
            href={PATH.CONTENTS_MANAGER_INDEX}
            Icon={Icon.Image}
            collapsed={collapsed}
          >
            <SubMenuItem title={t('gnb.menu.콘텐츠')} href={PATH.CONTENTS_MANAGER_CONTENTS_INDEX} />
            <SubMenuItem title={t('gnb.menu.이미지')} href={PATH.CONTENTS_MANAGER_LIBRARIES_INDEX} />
            <SubMenuItem title={t('gnb.menu.상품')} href={PATH.CONTENTS_MANAGER_PRODUCTS_INDEX} />
          </MenuItem>
          <MenuItem
            title={t('gnb.menu.대시보드')}
            href={PATH.DASHBOARD_INDEX}
            Icon={Icon.Dashboard}
            collapsed={collapsed}
          >
            <SubMenuItem title={t('gnb.menu.dashboard.campaign_analysis')} href={PATH.DASHBOARD_CAMPAIGN_ANALYSIS} />
            <SubMenuItem
              title={t('gnb.menu.dashboard.sending_set_analysis')}
              href={PATH.DASHBOARD_SENDING_SET_ANALYSIS}
            />
            <SubMenuItem
              title={t('gnb.menu.dashboard.audience_analysis')}
              href={PATH.DASHBOARD_AUDIENCE_ANALYSIS}
            />
            {MainSetting.showDashboardCustomers && <SubMenuItem title={t('gnb.menu.dashboard.customers')} href={PATH.DASHBOARD_CUSTOMERS} />}
            {MainSetting.showCustomerAnalysisAgent && (
              <SubMenuItem title={`AI 에이전트`} href={PATH.DASHBOARD_AGENT} />
            )}
          </MenuItem>
        </ul>
      </div>
      <ProfileItem collapsed={collapsed} />
    </SidebarContainer>
  );
};

export default MainMenuView;
